import { Suspense, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom-v5-compat';
import { Toaster } from 'sonner';

import { ImpersonationBanner } from '../features/impersonation/ImpersonationBanner';
import { useIsAdmin } from '../hooks/useCurrentUser';
import { useMobileApp } from '../hooks/useMobileApp';
import { cn } from '../lib/cn';
import { Header } from './Header';
import { MOBILE_HOME, isMobileAllowedPath } from './nav';
import { Sidebar } from './Sidebar';
import { ThemeProvider, useTheme } from './theme';

// In the installed mobile PWA, only routes flagged `mobile` in nav.ts are
// reachable. Anything else (deep links, manual nav, the /v2 index redirect)
// bounces to MOBILE_HOME. On desktop this is a no-op pass-through.
function MobileGuard({ children }: { children: ReactNode }) {
    const isMobileApp = useMobileApp();
    const isAdmin = useIsAdmin();
    const { pathname } = useLocation();
    // Admins roam freely on mobile; everyone else is pinned to the mobile-enabled
    // routes (Tickete + Rapoarte zilnice).
    if (isMobileApp && !isAdmin && !isMobileAllowedPath(pathname)) {
        return <Navigate to={MOBILE_HOME} replace />;
    }
    return <>{children}</>;
}

function ShellInner() {
    const { theme } = useTheme();
    const [isSidebarOpen, setSidebarOpen] = useState(false);

    return (
        <div className={cn(theme === 'dark' && 'dark')}>
            <div className="v2-root flex h-screen overflow-hidden bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
                <Toaster
                    richColors
                    closeButton
                    position="top-right"
                    theme={theme}
                />
                <Sidebar isOpen={isSidebarOpen} onNavigate={() => setSidebarOpen(false)} />
                <div className="flex min-w-0 flex-1 flex-col">
                    <Header onToggleSidebar={() => setSidebarOpen((open) => !open)} />
                    <ImpersonationBanner />
                    <main className="flex-1 overflow-y-auto">
                        <Suspense
                            fallback={
                                <div className="flex h-64 w-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                                    Se incarca...
                                </div>
                            }
                        >
                            <MobileGuard>
                                <Outlet />
                            </MobileGuard>
                        </Suspense>
                    </main>
                </div>
            </div>
        </div>
    );
}

export function Shell() {
    return (
        <ThemeProvider>
            <ShellInner />
        </ThemeProvider>
    );
}
